if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      priceShopifyContainer = null;
      judgeMeWidget = null;
      variantMapByValues = null;
      variantById = null;
      variantsArr = null;
      storeWideStateHandler = null;

      cacheVariants() {
        const vs = this.variantSelectors;
        if (!vs || this.variantMapByValues) return;

        this.variantsArr = JSON.parse(vs.dataset.variants || '[]');
        this.variantById = new Map(this.variantsArr.map(v => [v.id, v]));

        // key like "Flavor|Size|..." -> id
        this.variantMapByValues = new Map();
        for (const v of this.variantsArr) {
          this.variantMapByValues.set(v.options.join('|'), v.id);
        }
      }

      getSelectedValuesFromDOM() {
        const vs = this.variantSelectors;
        if (!vs) return [];
        const valuesByIndex = new Map();
        vs.querySelectorAll('input[type="radio"][data-index]').forEach(inp => {
          if (inp.checked) valuesByIndex.set(inp.dataset.index, inp.dataset.value);
        });
        vs.querySelectorAll('select[data-index]').forEach(sel => {
          if (sel.value != null) valuesByIndex.set(sel.dataset.index, sel.value);
        });
        const out = [];
        for (let i = 1; i <= 6; i++) out.push(valuesByIndex.get(String(i)) ?? null);
        return out;
      }

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
      }

      connectedCallback() {
        try {
          const _add = EventTarget.prototype.addEventListener;
          if (!_add.__pup_patched__) {
            EventTarget.prototype.addEventListener = function(type, listener, opts) {
              if ((type === 'touchstart' || type === 'touchmove' || type === 'wheel') && (opts === undefined || opts === false)) {
                return _add.call(this, type, listener, { passive: true });
              }
              return _add.call(this, type, listener, opts);
            };
            EventTarget.prototype.addEventListener.__pup_patched__ = true;
          }
        } catch(e){}
        this.cacheVariants();
        this.initializeProductSwapUtility();

        this.storeWideStateHandler = () => this.renderSizeBadgesUsingVariantWeight();
        document.addEventListener("revlift:storewide-state", this.storeWideStateHandler);

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true })
        );

        let imageContainer = this.querySelector(".product__media");
        this.priceShopifyContainer = this.querySelector(".price-shopify-container");
        this.judgeMeWidget =
          this.querySelector(".jdgm-preview-badge") ||
          document.querySelector(".jdgm-preview-badge");

        this.applyZoomFunctionality(imageContainer);

        this.moveJudgeMeWidget();

        const carouselImages = this.querySelectorAll(
          ".thumbnail-list__item button"
        );
        carouselImages.forEach((carouselImage) => {
          carouselImage.addEventListener("click", () => {
            const mediaTarget =
              carouselImage.parentElement.getAttribute("data-target");
            const targetMedia = this.querySelector(
              `[data-media-id="${mediaTarget}"]`
            );
            const mainImageContainer =
              targetMedia.querySelector(".product__media");

            this.applyZoomFunctionality(mainImageContainer);
          });
        });

        // This function manages tab selection, showing the corresponding content and hiding others when a tab is clicked.
        let variantId = "";
        const urlParams = new URLSearchParams(window.location.search);
        variantId = urlParams.get("variant");

        if (!variantId) {
          const productTabsSection =
            document.querySelector(`.product-section `);
          variantId = productTabsSection.id.split("-")[1];

          if (!variantId) {
            return;
          }

          productTabsSection.classList.remove("hide-variant");
          productTabsSection.classList.add("show-variant");
          this.handleVariantsTabs(variantId, productTabsSection);
        }

        const section = document.querySelector(`#variant-${variantId}`);
        this.handleVariantsTabs(variantId, section);
        
        this.renderSizeBadgesUsingVariantWeight();
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      isMobile() {
        return window.matchMedia('(max-width: 768px)').matches;
      }

      fastApplyVariant(variant) {
        if (!variant) return;
        this.updateURL(this.dataset.url, variant.id);
        this.updateVariantInputs(variant.id);

        const ops = () => {
          const money = (c)=>`$${(c/100).toFixed(2)}`;
          const priceBox = this.querySelector(`#price-${this.sectionId}`);
          if (priceBox) {
            const priceEl   = priceBox.querySelector('.price-item--sale, .price-item--regular');
            const compareEl = priceBox.querySelector('.variant-item__old-price, .secondary-color-dark');
            if (priceEl)   priceEl.textContent = money(variant.price);
            if (compareEl) compareEl.textContent = variant.compare_at_price ? money(variant.compare_at_price) : '';
          }
          this.productForm?.toggleSubmitButton(!(variant.available ?? true), window.variantStrings.soldOut);
          const buyNowButton = this.querySelector('#buyNowButton');
          if (buyNowButton) buyNowButton.disabled = !(variant.available ?? true);
        };

        if ('requestAnimationFrame' in window) {
          requestAnimationFrame(ops);
        } else {
          ops();
        }

        if (variant.featured_media?.id) {
          this.querySelector(`media-gallery`)?.setActiveMedia?.(
            `${this.dataset.section}-${variant.featured_media.id}`, true
          );
        }
        this.renderSizeBadgesUsingVariantWeight?.();
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        if (this.storeWideStateHandler) {
          document.removeEventListener("revlift:storewide-state", this.storeWideStateHandler);
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) =>
              element.classList.add("scroll-trigger--cancel")
            )
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target } }) {
        if (!this.contains(event.target)) return;
        this.resetProductFormState();
        this.cacheVariants();

        const vs = this.variantSelectors;
        if (!vs || !this.variantsArr?.length) return;

        // always compute from DOM for robustness
        let selectedValues = this.getSelectedValuesFromDOM(); // ["Chicken","4 oz",...]

        // If Flavor changed → fallback to smallest valid Size when combo doesn't exist
        if (target?.dataset?.index === "1") {
          const key = selectedValues.filter(Boolean).join('|');
          let chosenId = this.variantMapByValues.get(key);

          if (!chosenId) {
            const flavor = selectedValues[0];
            // sort sizes by numeric parse if possible; fallback to string
            const sameFlavor = this.variantsArr.filter(v => v.options[0] === flavor);
            sameFlavor.sort((a,b) => {
              const ax = parseFloat(a.options[1]); const bx = parseFloat(b.options[1]);
              if (isFinite(ax) && isFinite(bx)) return ax - bx;
              return String(a.options[1]).localeCompare(String(b.options[1]), undefined, {numeric:true});
            });
            if (sameFlavor.length) {
              const fallback = sameFlavor[0];
              const sizeEl = vs.querySelector(`[data-index="2"][data-value="${fallback.options[1]}"]`);
              if (sizeEl) {
                sizeEl.checked = true;
                selectedValues = this.getSelectedValuesFromDOM();
              }
              this.updateSizeOptionUI(flavor);
            }
          }
        }

        // find chosen variant
        const chosenId = this.variantMapByValues.get(selectedValues.filter(Boolean).join('|'));
        const chosen   = chosenId ? this.variantById.get(chosenId) : null;

        const productUrl = target?.dataset?.productUrl || this.pendingRequestUrl || this.dataset.url;
        const swappingProduct = this.dataset.url !== productUrl;

        // **FAST PATH on mobile**: same product ⇒ no fetch
        if (this.isMobile() && !swappingProduct) {
          this.fastApplyVariant(chosen);
          // focus restore for accessibility
          requestAnimationFrame(() => document.querySelector(`#${target.id}`)?.focus());
          return;
        }

        // desktop OR cross-product: use your existing partial/full fetch path
        this.pendingRequestUrl = productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === "true" && swappingProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            /* optionValues unused now */ null,
            shouldFetchFullPage,
            chosen?.id || null
          ),
          targetId: target.id,
          callback: swappingProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.log("Fetch aborted by user");
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "variant-selects [data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false, variantId) {
        const u = new URL(url, window.location.origin);
        if (!shouldFetchFullPage) u.searchParams.set('section_id', this.sectionId);
        if (variantId)            u.searchParams.set('variant', variantId); // <- key line
        if (optionValues?.length) u.searchParams.set('option_values', optionValues.join(','));
        return `${u.pathname}?${u.searchParams.toString()}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector("variant-selects");
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          this.moveJudgeMeWidget();
          const variant = this.getSelectedVariant(html);
          const productTabsSection = Array.from(
            document.querySelectorAll(".product-section")
          ).filter((section) => section.id.includes("variant"));

          productTabsSection.forEach((section) => {
            if (section.id === `variant-${variant.id}`) {
              section.classList.add("show-variant");
              section.classList.remove("hide-variant");
            } else {
              section.classList.add("hide-variant");
              section.classList.remove("show-variant");
            }
          });

          // Updating the product tabs section to reflect the selected product variant and display the corresponding content.
          const section = document.querySelector(`#variant-${variant.id}`);

          this.handleVariantsTabs(variant.id, section);

          // Applying 3D image scaling animation
          let imageContainer = html.querySelector(".product__media");
          this.applyZoomFunctionality(imageContainer);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.renderSizeBadgesUsingVariantWeight();
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`
            );
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden")
          );
          updateSourceFromDestination(
            "Inventory",
            ({ innerText }) => innerText === ""
          );
          updateSourceFromDestination("Volume");
          updateSourceFromDestination("Price-Per-Item", ({ classList }) =>
            classList.contains("hidden")
          );

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove("hidden");
          this.querySelector(
            `#Volume-Note-${this.dataset.section}`
          )?.classList.remove("hidden");

          const sourceSubmitButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          // Offer initialization also disables the server-rendered button; it is not an inventory signal.
          const isSoldOut = !variant.available || !sourceSubmitButton ||
            sourceSubmitButton.hasAttribute('data-out-in-stock') ||
            (!sourceSubmitButton.hasAttribute('data-revlift-offer-gated') && sourceSubmitButton.hasAttribute('disabled'));

          this.productForm?.toggleSubmitButton(isSoldOut, window.variantStrings.soldOut);

          const buyNowButton = this.querySelector('#buyNowButton');

          if (buyNowButton) {
            buyNowButton.disabled = isSoldOut;
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ""}`
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`
        );
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Price-Per-Item",
          "Volume-Note",
          "Volume",
          "Quantity-Rules",
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(html, variantFeaturedMediaId) {
        console.log("Attempting to update media");
        if (!variantFeaturedMediaId) return;
        if (this.isMobile()) {
          // Only flip active slide; avoid the heavy gallery diffing
          if (variantFeaturedMediaId) {
            this.querySelector(`media-gallery`)?.setActiveMedia?.(
              `${this.dataset.section}-${variantFeaturedMediaId}`, true
            );
          }
          return;
        }
        document.querySelectorAll('.image-magnify-full-size').forEach(n => n.remove());

        const mediaGallerySource = this.querySelector("media-gallery ul");
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute("data-zoom-on-hover")) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(
            mediaGallerySource.querySelectorAll("li[data-media-id]")
          );
          const sourceSet = new Set(
            mediaGallerySourceItems.map((item) => item.dataset.mediaId)
          );
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [
              item.dataset.mediaId,
              { item, index },
            ])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] =
            refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll("li[data-media-id]")
          );
          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId)
          );
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (
              !sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)
            ) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (
              !destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)
            ) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh)
            [mediaGallerySourceItems, sourceSet, sourceMap] =
              refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach(
            (destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(
                    `li:nth-of-type(${destinationIndex + 1})`
                  )
                );

                // refresh source now that it has been modified
                [mediaGallerySourceItems, sourceSet, sourceMap] =
                  refreshSourceData();
              }
            }
          );
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(
          `.product-media-modal__content`
        );
        const newModalContent = html.querySelector(
          `product-modal .product-media-modal__content`
        );
        if (modalContent && newModalContent)
          modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner"
        ).classList.remove("hidden");
        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner"
            ).classList.add("hidden")
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label",
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step",
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }
      /**
       * Disable (and add a CSS class) to any size-option input
       * that doesn’t have a matching variant for the given flavor.
       *
       * @param {string} flavorValue  – the newly selected flavor name
       */
      updateSizeOptionUI(flavorValue) {
        const variants = JSON.parse(this.variantSelectors.dataset.variants);
        // all inputs whose data-index="2" (i.e. size pickers)
        const sizeInputs = this.variantSelectors.querySelectorAll(
          'input[data-index="2"]'
        );
      
        sizeInputs.forEach((input) => {
          const sizeValue = input.dataset.value;
          const isValid = variants.some(
            (v) =>
              v.options[0] === flavorValue && // option1 === flavor
              v.options[1] === sizeValue      // option2 === size
          );
      
          input.disabled = !isValid;
          const label = this.variantSelectors.querySelector(
            `label[for="${input.id}"]`
          );
          if (label) label.classList.toggle("size-unavailable", !isValid);
        });
      }
      getVariantDiscountMap() {
        if (this._variantDiscountMap) return this._variantDiscountMap;
        const el =
          document.getElementById(`VariantDiscounts-${this.sectionId}`) ||
          this.querySelector(`#VariantDiscounts-${this.sectionId}`) ||
          document.querySelector('[id^="VariantDiscounts-"]');
        this._variantDiscountMap = el ? JSON.parse(el.textContent) : {};
        return this._variantDiscountMap;
      }
      getVariantUpsellBadgeTextMap() {
        if (this._variantUpsellBadgeTextMap) return this._variantUpsellBadgeTextMap;

        const el =
          document.getElementById(`VariantUpsellBadgeText-${this.sectionId}`) ||
          this.querySelector(`#VariantUpsellBadgeText-${this.sectionId}`) ||
          document.querySelector('[id^="VariantUpsellBadgeText-"]');

        this._variantUpsellBadgeTextMap = el ? JSON.parse(el.textContent) : {};
        return this._variantUpsellBadgeTextMap;
      }
      // ADD THIS METHOD INSIDE THE CLASS
      renderSizeBadgesUsingVariantWeight() {
        try {
          const vs = this.variantSelectors;
          if (!vs) return;

          const variants = JSON.parse(vs.dataset.variants || '[]');
          if (!variants.length) return;

          const discounts = this.getVariantDiscountMap();
          const upsellTexts = this.getVariantUpsellBadgeTextMap();

          // current selections by option index (DOM is 1-based)
          const getSel = (i1) =>
            vs.querySelector(`input[data-index="${i1}"]:checked`)?.dataset.value ||
            vs.querySelector(`select[data-index="${i1}"]`)?.value || null;
          const selected = Array.from({ length: 6 }, (_, i) => getSel(i + 1));

          // find a variant matching all other selections, but with this group's value
          const findVariantFor = (slot0, value) => {
            let m = variants.find(v => {
              if (v.options[slot0] !== value) return false;
              for (let i = 0; i < v.options.length; i++) {
                if (i === slot0) continue;
                const want = selected[i];
                if (want && v.options[i] !== want) return false;
              }
              return true;
            });
            return m || variants.find(v => v.options[slot0] === value) || null;
          };

          // fieldsets to annotate: Size + Quantity-like
          const groups = [...vs.querySelectorAll('fieldset')].filter(fs => {
            const t = fs.querySelector('legend')?.textContent?.trim() || '';
            return /(size|quantity|qty|pack|count|denom|denomination|amount)/i.test(t);
          });

          groups.forEach(fs => {
            const inputs = [...fs.querySelectorAll('input[type="radio"]')];
            if (!inputs.length) return;
            const idx1 = parseInt(inputs[0].dataset.index, 10) || 1;
            const slot0 = idx1 - 1;

            inputs.forEach(input => {
              const value = input.dataset.value;
              const v = findVariantFor(slot0, value);
              const label = vs.querySelector(`label[for="${input.id}"]`) || input.closest('label');
              if (!v || !label) return;

              const txtRaw = (upsellTexts?.[v.id] || '').trim();
              const pct = Number(discounts?.[v.id]) || 0;
              const bestPct = window.RevLiftPricing?.bestDiscountPercent
                ? window.RevLiftPricing.bestDiscountPercent(pct, {
                    purchaseOptionMode: 'one_time',
                    element: this,
                    variantId: v.id,
                    quantity: 1,
                    subtotalCents: Number(v.price || 0)
                  })
                : pct;

              // ONLY allow txtRaw for gift-card store credit badges
              const isStoreCredit =
                txtRaw &&
                (/store\s*credit/i.test(txtRaw) || /^\s*\+\s*\$/.test(txtRaw));

              label.querySelector('.upsell-badge')?.remove();
              label.style.position = 'relative';
              label.style.overflow = 'visible';

              if (isStoreCredit) {
                const badge = document.createElement('span');
                badge.className = 'upsell-badge';

                const vSpan = document.createElement('span');
                vSpan.className = 'upsell-badge-pct';
                vSpan.textContent = txtRaw; // "+$10 store credit"

                badge.appendChild(vSpan);
                label.appendChild(badge);

              } else if (bestPct > 0) {
                const badge = document.createElement('span');
                badge.className = 'upsell-badge';

                const w = document.createElement('span');
                w.className = 'upsell-badge-word';
                w.textContent = 'Save\u00A0';

                const vSpan = document.createElement('span');
                vSpan.className = 'upsell-badge-pct';
                vSpan.textContent = `${Math.round(bestPct * 100) / 100}%`;

                badge.appendChild(w);
                badge.appendChild(vSpan);
                label.appendChild(badge);
              }
            });
          });
        } catch (err) {
          console.warn('renderSizeBadgesUsingVariantWeight error:', err);
        }
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products"
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list"
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      applyZoomFunctionality(imageContainer) {
        const zoomLens = imageContainer.querySelector(".zoom-lens");
        const lens = imageContainer.querySelector(".lens");
        const image = imageContainer.querySelector("img");
        if (!image) return;

        const enableZoom = () => {
          if (!zoomLens || !lens || !image) return;
          image.classList.add('image-magnify-hover');
          image.addEventListener("mousemove", handleMouseMove);
          image.addEventListener("mouseleave", handleMouseLeave);
          image.addEventListener("mouseenter", handleMouseEnter);
        };

        const disableZoom = () => {
          if (!zoomLens || !lens || !image) return;
          image.classList.remove("image-magnify-hover");
          image.removeEventListener("mousemove", handleMouseMove);
          image.removeEventListener("mouseleave", handleMouseLeave);
          image.removeEventListener("mouseenter", handleMouseEnter);
          zoomLens.style.display = "none";
          lens.style.visibility = "hidden";
        };

        const handleMouseMove = (e) => {
          const rect = image.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;

          if (!zoomLens || !lens) return;
          zoomLens.style.display = "block";
          zoomLens.style.backgroundImage = `url(${image.src})`;
          zoomLens.style.backgroundSize = `${image.width * 2}px ${
            image.height * 2
          }px`;

          const zoomX = (x / image.width) * 100;
          const zoomY = (y / image.height) * 100;
          zoomLens.style.backgroundPosition = `${zoomX}% ${zoomY}%`;

          const lensX = x - lens.offsetWidth / 2;
          const lensY = y - lens.offsetHeight / 2;

          const maxX = image.clientWidth - lens.offsetWidth;
          const maxY = image.clientHeight - lens.offsetHeight;

          lens.style.left = Math.max(0, Math.min(lensX, maxX)) + "px";
          lens.style.top = Math.max(0, Math.min(lensY, maxY)) + "px";
          lens.style.visibility = "visible";
        };

        const handleMouseLeave = () => {
          if (!zoomLens || !lens) return;
          zoomLens.style.display = "none";
          lens.style.visibility = "hidden";
        };

        const handleMouseEnter = () => {
          lens.style.visibility = "visible";
        };

        const updateZoomFunctionality = () => {
          if (window.innerWidth <= 576) {
            disableZoom();
          } else {
            enableZoom();
          }
        };

        updateZoomFunctionality();

        window.addEventListener("resize", updateZoomFunctionality);
      }

      handleVariantsTabs(variantId, section) {
        if (!section) {
          return;
        }

        // Get only visible tabs and their corresponding content
        const tabs = section.querySelectorAll(".tabs-content input[type='radio']");
        const tabsContent = section.querySelectorAll(".tab-content");

        // Validate that we have tabs and content
        if (tabs.length === 0 || tabsContent.length === 0) {
          return;
        }

        // Reset all tabs and content
        tabsContent.forEach((tab) => (tab.style.display = "none"));
        tabs.forEach((radio) => (radio.checked = false));

        // Show first available tab
        tabs[0].checked = true;
        tabsContent[0].style.display = "block";

        // Add event listeners using data attributes for better mapping
        tabs.forEach((tab, idx) => {
          // Remove existing listeners to prevent duplicates
          tab.removeEventListener('click', tab._clickHandler);
          
          // Create new handler
          const clickHandler = () => {
            // Reset all tabs
            tabs.forEach((radio) => {
              radio.checked = false;
            });
            
            tabsContent.forEach((content) => {
              content.style.display = "none";
            });

            // Activate clicked tab
            tab.checked = true;
            
            // Show corresponding content using data attribute or index mapping
            const contentId = tab.getAttribute('data-content') || `content-${variantId}-${this.getTabNumber(tab.id)}`;
            const targetContent = section.querySelector(`#${contentId}`) || tabsContent[idx];
            
            if (targetContent) {
              targetContent.style.display = "block";
            }
          };
          
          // Store handler reference and add listener
          tab._clickHandler = clickHandler;
          tab.addEventListener('click', clickHandler);
        });
      }

      // Helper method to extract tab number from ID
      getTabNumber(tabId) {
        const match = tabId.match(/-(\d+)$/);
        return match ? match[1] : '1';
      }

      moveJudgeMeWidget() {
        // Mobile CLS is sensitive to delayed top-of-page DOM moves.
        // Keep app badge in-place on mobile; only normalize placement on desktop.
        if (this.isMobile()) return;
        if (!this.priceShopifyContainer || !this.judgeMeWidget) return;
        if (this.judgeMeWidget.parentElement !== this.priceShopifyContainer) {
          this.priceShopifyContainer.appendChild(this.judgeMeWidget);
        }
      }
    }
  );
}
